/**
 * Converts opacity value to Tailwind CSS class
 * @param {number} opacity - Opacity value (0-100)
 * @returns {string} - Tailwind CSS opacity class
 */

import { transformColor } from "./generalUtils";

// Constants for opacity values
const TAILWIND_OPACITY_VALUES = [
  0, 5, 10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 95, 100,
];
const DEFAULT_OPACITY = 100;

// Constants for border styles
const BORDER_STYLES = {
  none: "border-0",
  dashed: "border-dashed",
  dotted: "border-dotted",
  solid: "border-solid",
};

// Constants for border widths
const BORDER_WIDTHS = {
  0: "border-0",
  0.5: "border",
  1: "border-2",
  2: "border-4",
  8: "border-8",
};

// Constants for shadow sizes
const SHADOW_SIZES = {
  1: "shadow-sm",
  2: "shadow",
  3: "shadow-md",
  4: "shadow-lg",
  5: "shadow-xl",
  6: "shadow-2xl",
};

// Constants for glow and shadow
const DEFAULT_GLOW_SIZE = 10;
const DEFAULT_GLOW_INTENSITY = 50;
const DEFAULT_GLOW_OPACITY = 100;
const DEFAULT_SHADOW_OPACITY = 100;
const DEFAULT_SHADOW_INTENSITY = 50;

/**
 * Gets the closest Tailwind opacity value
 */
function getClosestOpacityValue(opacity) {
  return TAILWIND_OPACITY_VALUES.reduce((prev, curr) =>
    Math.abs(curr - opacity) < Math.abs(prev - opacity) ? curr : prev,
  );
}

/**
 * Converts opacity value to Tailwind CSS class
 */
export function getOpacityClass(opacity) {
  if (opacity === undefined || opacity === null) {
    return `opacity-${DEFAULT_OPACITY}`;
  }

  return TAILWIND_OPACITY_VALUES.includes(opacity)
    ? `opacity-${opacity}`
    : `opacity-[${(opacity / 100).toFixed(2)}]`;
}

/**
 * Converts border properties to Tailwind CSS classes
 * @param {Object} border - Border properties object
 * @returns {string} - Space-separated Tailwind CSS classes
 */
export function getBorderClasses(border) {
  if (!border || !border.border_enabled) {
    return "border-0";
  }

  const classes = [
    getBorderWidthClass(border.border_width),
    getBorderStyleClass(border.border_style),
  ];

  if (border.border_color) {
    classes.push(
      border.border_color.startsWith("#")
        ? "border-custom"
        : `border-${border.border_color}`,
    );
  }

  if (border.border_opacity !== undefined && border.border_opacity !== null) {
    classes.push(
      `border-opacity-${getClosestOpacityValue(border.border_opacity)}`,
    );
  }

  return classes.join(" ");
}

/**
 * Gets border width class based on width value
 */
function getBorderWidthClass(width) {
  if (width === undefined || width === null) {
    return "border";
  }

  if (width === 0) return BORDER_WIDTHS[0];
  if (width <= 0.5) return BORDER_WIDTHS[0.5];
  if (width <= 1) return BORDER_WIDTHS[1];
  if (width <= 2) return BORDER_WIDTHS[2];
  return BORDER_WIDTHS[8];
}

/**
 * Gets border style class
 */
function getBorderStyleClass(style) {
  return BORDER_STYLES[style] || BORDER_STYLES.solid;
}

/**
 * Map a BaseStyle border_width value to a pixel size that roughly matches the
 * desired "stroke" feel for text outlines.
 *
 * In the editor we expose border width as a 1..10 scale. For text outlines we
 * want much finer control than Tailwind's discrete border steps, and we want
 * level 10 to be roughly equivalent to the old "very thick" outline.
 */
function borderWidthToPx(width) {
  if (width === undefined || width === null) return 1;
  const w = Number(width);
  if (!Number.isFinite(w) || w <= 0) return 0;

  // Map 1..10 -> 0.5..4px (clamped). This keeps early levels usable and makes
  // the top end comparable to the previous max stroke we produced.
  const px = (w / 10) * 4;
  return Math.max(0.5, Math.min(4, px));
}

/**
 * Convert an arbitrary color token (hex or Tailwind token) to `rgba(r,g,b,a)`.
 */
function normalizeToRgba(color, opacityPercent = 100) {
  const alpha = Math.max(0, Math.min(1, Number(opacityPercent ?? 100) / 100));
  if (!color) return `rgba(0, 0, 0, ${alpha})`;

  // Already an rgb/rgba string
  if (typeof color === "string" && (color.startsWith("rgba(") || color.startsWith("rgb("))) {
    // If it's already rgba/rgb, keep it as-is (we don't reliably parse/override alpha here).
    return color;
  }

  let rgb = "0, 0, 0";
  if (typeof color === "string" && color.startsWith("#")) {
    rgb = hexToRgb(color);
  } else {
    try {
      const hex = transformColor(color, 100, "hex");
      if (typeof hex === "string" && hex.startsWith("#")) {
        rgb = hexToRgb(hex);
      }
    } catch (_) {}
  }
  return `rgba(${rgb}, ${alpha})`;
}

/**
 * Normalize any color token (e.g., tailwind like "blue-600" or rgba) to RGB "r, g, b"
 */
function normalizeToRgb(color) {
  if (!color) return "0, 0, 0";
  if (typeof color === "string" && color.startsWith("#")) {
    return hexToRgb(color);
  }
  try {
    const hex = transformColor(color, 100, "hex");
    if (typeof hex === "string" && hex.startsWith("#")) {
      return hexToRgb(hex);
    }
  } catch (_) {}
  return "0, 0, 0";
}

/**
 * Creates CSS for glow effect (not directly available in Tailwind)
 * @param {Object} glow - Glow properties object
 * @returns {Object} - Object with className and style properties
 */
export function getGlowStyles(glow, isText = false) {
  if (!glow || !glow.glow_enabled || !glow.glow_color) {
    return { className: "", style: {} };
  }

  const size = Number(glow.glow_size) || 0;
  // Respect size 0/null: render nothing
  if (size <= 0) {
    return { className: "", style: {} };
  }
  const opacity = (glow.glow_opacity || DEFAULT_GLOW_OPACITY) / 100;
  const intensity = glow.glow_intensity || DEFAULT_GLOW_INTENSITY;
  const blurRadius = size;
  const spreadRadius = Math.round((intensity - 50) / 2);

  const directions = glow.glow_directions
    ? glow.glow_directions.replace(/\s+/g, "").split(",")
    : ["all"];

  // For text, use text-shadow to avoid box background artifacts
  if (isText) {
    const rgb = normalizeToRgb(glow.glow_color);
    const color = `rgba(${rgb}, ${opacity})`;
    const allDirs = [
      "top",
      "right",
      "bottom",
      "left",
      "top-left",
      "top-right",
      "bottom-left",
      "bottom-right",
    ];
    const dirs =
      directions.includes("all") || directions.includes("center")
        ? allDirs
        : directions;

    const textShadowParts = dirs.map((dir) => generateTextShadowPart(dir, size, blurRadius, color));
    return {
      className: "",
      style: { textShadow: textShadowParts.filter(Boolean).join(", ") },
    };
  }

  // Non-text: use box-shadow (halo around element)
  let shadowParts = [];
  if (directions.includes("all")) {
    const allDirs = [
      "top",
      "right",
      "bottom",
      "left",
      "top-left",
      "top-right",
      "bottom-left",
      "bottom-right",
    ];
    shadowParts = allDirs.map((dir) =>
      generateShadowPart(
        dir,
        size,
        blurRadius,
        spreadRadius,
        normalizeToRgb(glow.glow_color),
        opacity,
      ),
    );
  } else {
    shadowParts = directions.map((dir) =>
      generateShadowPart(
        dir,
        size,
        blurRadius,
        spreadRadius,
        normalizeToRgb(glow.glow_color),
        opacity,
      ),
    );
  }

  return {
    className: "",
    style: { boxShadow: shadowParts.filter(Boolean).join(", ") },
  };
}

/**
 * Generates shadow parts for a given direction
 */
function generateShadowPart(
  direction,
  size,
  blurRadius,
  spreadRadius,
  color,
  opacity,
) {
  const shadows = {
    center: `0 0 ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    top: `0 -${size}px ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    right: `${size}px 0 ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    bottom: `0 ${size}px ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    left: `-${size}px 0 ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    "top-left": `-${size}px -${size}px ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    "top-right": `${size}px -${size}px ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    "bottom-left": `-${size}px ${size}px ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
    "bottom-right": `${size}px ${size}px ${blurRadius}px ${spreadRadius}px rgba(${color}, ${opacity})`,
  };

  return shadows[direction] || "";
}

/**
 * Generates text-shadow part for a given direction (no spread for text)
 */
function generateTextShadowPart(direction, offset, blurRadius, color) {
  const parts = {
    center: `0 0 ${blurRadius}px ${color}`,
    top: `0 -${offset}px ${blurRadius}px ${color}`,
    right: `${offset}px 0 ${blurRadius}px ${color}`,
    bottom: `0 ${offset}px ${blurRadius}px ${color}`,
    left: `-${offset}px 0 ${blurRadius}px ${color}`,
    "top-left": `-${offset}px -${offset}px ${blurRadius}px ${color}`,
    "top-right": `${offset}px -${offset}px ${blurRadius}px ${color}`,
    "bottom-left": `-${offset}px ${offset}px ${blurRadius}px ${color}`,
    "bottom-right": `${offset}px ${offset}px ${blurRadius}px ${color}`,
  };
  return parts[direction] || "";
}

/**
 * Creates CSS for shadow effect
 * @param {Object} shadow - Shadow properties object
 * @returns {Object} - Object with className and style properties
 */
export function getShadowStyles(shadow) {
  if (!shadow || !shadow.shadow_enabled) {
    return { className: "", style: {} };
  }

  if (!shadow.shadow_custom_enabled) {
    const size = shadow.shadow_default_size || 0;
    // For text, use text-shadow instead of classes
    if (shadow.isText) {
      if (size <= 0) return { className: "", style: {} };
      const offset = size; // px
      // Slightly larger blur to soften
      const blur = Math.max(2, size * 3); // px
      // Make default less intense by factoring intensity slider
      const intensityFactor = (shadow.shadow_intensity || DEFAULT_SHADOW_INTENSITY) / 100; // 0..1
      const baseAlpha = (shadow.shadow_opacity || DEFAULT_SHADOW_OPACITY) / 100; // 0..1
      const alpha = Math.max(0, Math.min(1, baseAlpha * intensityFactor * 0.6));
      const color = `rgba(0, 0, 0, ${alpha})`;
      const allDirs = [
        "top",
        "right",
        "bottom",
        "left",
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
      ];
      // Center part with an even softer alpha
      const centerAlpha = Math.max(0, Math.min(1, alpha * 0.6));
      const parts = [
        generateTextShadowPart("center", 0, blur, `rgba(0, 0, 0, ${centerAlpha})`),
        ...allDirs.map((d) => generateTextShadowPart(d, offset, blur, color)),
      ].filter(Boolean);
      return { className: "", style: { textShadow: parts.join(", ") } };
    }
    const className = SHADOW_SIZES[size] || "";
    return { className, style: {} };
  }

  const size = shadow.shadow_custom_size || 0;
  const directions = shadow.shadow_directions
    ? shadow.shadow_directions
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : ["right", "left"];
  const opacity = (shadow.shadow_opacity || DEFAULT_SHADOW_OPACITY) / 100;
  const intensity = (shadow.shadow_intensity || DEFAULT_SHADOW_INTENSITY) / 100;
  const shadowSize = size * intensity;

  // Text: use textShadow; Non-text: boxShadow
  if (shadow.isText) {
    if (shadowSize <= 0) return { className: "", style: {} };
    // Scale opacity by intensity to keep perceived strength consistent across multiple directions
    const scaledOpacity = Math.max(0, Math.min(1, opacity * Math.max(0.25, intensity)));
    const color = `rgba(0, 0, 0, ${scaledOpacity})`;
    const dirs = directions.includes("all")
      ? [
          "top",
          "right",
          "bottom",
          "left",
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right",
        ]
      : directions;
    const parts = dirs.map((dir) => generateTextShadowPart(dir, shadowSize, size, color));
    return { className: "", style: { textShadow: parts.filter(Boolean).join(", ") } };
  }

  const shadowParts = directions.map((dir) => {
    if (dir === "all") {
      const allDirs = [
        "top",
        "right",
        "bottom",
        "left",
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
      ];
      const scaledOpacity = Math.max(0, Math.min(1, opacity * Math.max(0.25, intensity)));
      return allDirs
        .map((d) => generateShadowPart(d, shadowSize, size, 0, "0, 0, 0", scaledOpacity))
        .filter(Boolean)
        .join(", ");
    }
    const scaledOpacity = Math.max(0, Math.min(1, opacity * Math.max(0.25, intensity)));
    return generateShadowPart(dir, shadowSize, size, 0, "0, 0, 0", scaledOpacity);
  });

  return {
    className: "",
    style: { boxShadow: shadowParts.filter(Boolean).join(", ") },
  };
}

/**
 * Helper function to convert hex color to RGB
 * @param {string} hex - Hex color code
 * @returns {string} - RGB value as "r, g, b"
 */
export function hexToRgb(hex) {
  if (!hex) return "0, 0, 0";

  hex = hex.replace("#", "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
}

/**
 * Combines all base style properties into Tailwind classes and custom styles
 * @param {Object} baseStyle - The BaseStyle object with all styling properties
 * @returns {Object} - Object with className for Tailwind classes and style for custom CSS
 */
export function getBaseStyleClasses(baseStyle, isText = false) {
  if (!baseStyle) {
    return { className: "", style: {} };
  }

  const opacityClass = getOpacityClass(baseStyle.opacity);
  let borderClasses = getBorderClasses({
    border_enabled: baseStyle.border_enabled,
    border_width: baseStyle.border_width,
    border_style: baseStyle.border_style,
    border_color: baseStyle.border_color,
    border_opacity: baseStyle.border_opacity,
  });

  // For text elements (Title/Text), interpret BaseStyle "border" as a text outline
  // so it surrounds glyphs rather than the element box.
  let textStrokeClassName = "";
  let textStrokeStyle = {};
  if (isText && baseStyle.border_enabled) {
    const strokePx = borderWidthToPx(baseStyle.border_width);
    if (strokePx > 0) {
      textStrokeClassName = "pc-text-stroke";
      textStrokeStyle = {
        "--pc-text-stroke-width": `${strokePx}px`,
        "--pc-text-stroke-color": normalizeToRgba(
          baseStyle.border_color,
          baseStyle.border_opacity ?? 100,
        ),
      };
    }
    // Prevent box borders on text.
    borderClasses = "border-0";
  }

  const { className: glowClassName, style: glowStyle } = getGlowStyles({
    glow_enabled: baseStyle.glow_enabled,
    glow_color: baseStyle.glow_color,
    glow_size: baseStyle.glow_size,
    glow_intensity: baseStyle.glow_intensity,
    glow_opacity: baseStyle.glow_opacity,
    glow_directions: baseStyle.glow_directions,
  }, isText);

  const { className: shadowClassName, style: shadowStyle } = getShadowStyles({
    ...baseStyle,
    isText,
  });

  const className = [
    opacityClass,
    borderClasses,
    glowClassName,
    shadowClassName,
    textStrokeClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const style = {
    ...glowStyle,
    ...shadowStyle,
    ...textStrokeStyle,
  };

  // Non-text elements can still use inline borderColor when border_color is hex.
  // Text elements use the stroke vars instead (see above).
  if (!isText && baseStyle.border_enabled && baseStyle.border_color?.startsWith("#")) {
    const opacity =
      baseStyle.border_opacity !== undefined &&
      baseStyle.border_opacity !== null
        ? Math.round((baseStyle.border_opacity * 255) / 100)
            .toString(16)
            .padStart(2, "0")
        : "";
    style.borderColor = `${baseStyle.border_color}${opacity}`;
  }

  return { className, style };
}
